import React, {useEffect} from 'react';
import {Box, Typography, Alert, Button} from '@mui/material';
import {useSnackbar} from 'notistack';
import testAccounts from '../../testAccounts.json';
import regressionAccounts from '../../regressionAccounts.json';
import {TestAccountsList} from './TestAccountsList';
import {useDeleteAccounts} from '../../hooks/useDeleteAccounts';
import {GET_ACCOUNT_REVIEWS} from '../AccountOverview/accountOverviewQueries';
import {useAccountContext} from '../../contexts/AccountContext';
import {FeatureBoundary} from '../../contexts/FeatureBoundary';
import {GlobalPermissionEnum, PermGroupsEnum} from '../../schema-types/globalTypes';
import {useGodMode} from './DebugModal';
import {AddBulkAccountDummyDataButton} from '../AccountOverview/AddAccountDummyDataButton';

interface QuickTestAccountsProps {
  onCloseAllModals?: () => void;
  separateActions?: boolean;
}

export const RegressionPage = () => {
  return (
    <FeatureBoundary
      features={[GlobalPermissionEnum.can_access_admin_features]}
      showErrorIfDenied
    >
      <Box p={2} bgcolor="white" borderRadius={2}>
        <QuickTestAccounts separateActions={false} onCloseAllModals={() => {}} />
      </Box>
    </FeatureBoundary>
  );
};

export const QuickTestAccounts: React.FC<QuickTestAccountsProps> = ({
  onCloseAllModals,
  separateActions,
}) => {
  const {
    deleteReviewsForRegressionAccounts,
    infoDeleteReviewsForRegressionAccounts,
    infoReviewsRegressionText,
    reviewsAccountDataLoading,
    infoLoading,
  } = useDeleteAccounts();
  const {accountId} = useAccountContext();
  const {enqueueSnackbar} = useSnackbar();
  const {onSetGroup} = useGodMode();

  useEffect(() => {
    infoDeleteReviewsForRegressionAccounts();
  }, [infoDeleteReviewsForRegressionAccounts]);

  useEffect(() => {
    // setting user back as god to allow smooth business unit switching
    const setGod = async () => {
      try {
        await onSetGroup(PermGroupsEnum.god);
      } catch (error) {
        enqueueSnackbar('Failed to set god permissions', {variant: 'error'});
        throw error;
      }
    };
    setGod();
  }, []);

  return (
    <Box>
      <TestAccountsList
        title="Regression Accounts"
        accountsData={regressionAccounts}
        onCloseAllModals={onCloseAllModals}
      />
      {/* <TestAccountsList
        title="Developer Accounts"
        accountsData={testAccounts}
        onCloseAllModals={onCloseAllModals}
      /> */}

      <Box
        mt={2}
        border="1px black solid"
        display="flex"
        flexDirection="column"
        padding={2}
      >
        <Typography>
          This button allows you to delete ALL reviews for ALL regression accounts! One of
          the BAs should click this button before any regression tests start.
        </Typography>
        <Alert severity="warning" sx={{marginBottom: '1rem'}}>
          {infoReviewsRegressionText}
        </Alert>
        <Button
          variant="contained"
          color="secondary"
          onClick={async () => {
            await deleteReviewsForRegressionAccounts({
              refetchQueries: accountId
                ? [
                    {
                      query: GET_ACCOUNT_REVIEWS,
                      variables: {
                        accountId,
                        idType: 'id',
                        curoSync: true,
                      },
                    },
                  ]
                : [],
            }).then(({data}) => {
              if (data?.deleteReviewsForRegressionAccounts?.ok) {
                enqueueSnackbar({
                  message: 'Deleted all reviews for all regression accounts',
                  variant: 'success',
                });
                return;
              }
              enqueueSnackbar({
                message: 'Could not delete all reviews for all regression accounts',
                variant: 'error',
              });
            });
          }}
        >
          Delete All Regression Account Reviews{' '}
          {reviewsAccountDataLoading || infoLoading ? 'Running...' : ''}
        </Button>
      </Box>

      <Box
        mt={2}
        border="1px black solid"
        display="flex"
        flexDirection="column"
        padding={2}
      >
        <Typography>
          This button adds dummy data (DOB, National Insurance, Nationality) to all
          contacts across all regression accounts to fix validation errors.
        </Typography>
        <Box mt={2}>
          <AddBulkAccountDummyDataButton accounts={regressionAccounts} />
        </Box>
      </Box>
    </Box>
  );
};
